import type { FilterDefault } from '@sanity/client/stega';

/**
 * Fields that carry the structure of a page rather than its text.
 *
 * These are the severe ones. `@sanity/client` skips `_`-prefixed fields, so `_key` and
 * `_type` are safe — but a page's blocks are stored flat and reassembled into a tree by
 * `groupPool` (`adapter.ts`) using two fields that are *not* `_`-prefixed and are not on
 * the client's denylist:
 *
 *   `parentKey`  matched against another block's `_key` to find its parent
 *   `area`       names the slot the block belongs to (`jss-section-main`, …)
 *
 * Encode either and the comparison silently fails: every block detaches from its parent
 * and lands at the page root, and slot names become unrecognizable to `slots.ts`. The page
 * still renders, wrongly, and only in draft mode. Reported by the CMS team, who see the
 * same break as "detached from parent" in the Studio.
 */
export const STRUCTURAL_FIELDS: ReadonlySet<string> = new Set(['parentKey', 'area']);

/**
 * Presentation settings — how a block looks, never what it says.
 *
 * Sitecore models these as rendering parameters, separate from field content. Sanity has
 * no such concept, so the migration bundle compiles each one into an ordinary field
 * sitting flat on the block, right next to real content (`Section` has `title` beside
 * `backgroundColor`). `useSetting` in `@eneco/cms` is the single reader.
 *
 * Every one of them is a style token, a variant name, a count or an id. None is ever shown
 * to a visitor, so excluding them costs no editing affordance — while encoding them breaks
 * a CSS value, a Sparky prop or a `=== 'primary'` comparison.
 *
 * This list is generated from the `useSetting('…')` call sites and pinned by
 * `stega-filter.spec.ts`, which fails if the two drift. Do not hand-edit: add the
 * `useSetting` call and re-run the test, which prints what to paste.
 */
export const PRESENTATION_FIELDS: ReadonlySet<string> = new Set([
  'CompactViewmode',
  'RenderVariant',
  'alignment',
  'anchorId',
  'backgroundColor',
  'backgroundColorBottom',
  'backgroundColorOption',
  'backgroundFirst',
  'backgroundSecond',
  'bringToFront',
  'budgetManagerAvailable',
  'buttonColor',
  'cardBackgroundOption',
  'chatLinksColumnCount',
  'chatLinksPlacement',
  'colSpan',
  'columnAlignment',
  'columnArrangement',
  'columns',
  'compactViewmode',
  'desktopVariant',
  'desktopVariantOption',
  'displayColumn',
  'footerBackgroundColor',
  'footerTextColor',
  'hasDivider',
  'hasFixedWidthOption',
  'headingLevel',
  'headingSize',
  'height',
  'hideArrows',
  'imagePosition',
  'isFilledCheckMark',
  'isFilledCheckmark',
  'isFollowedByNegativeMargin',
  'layout',
  'linkColor',
  'maxHeight',
  'minMargin',
  'mobileVariant',
  'mobileVariantOption',
  'objectFit',
  'renderVariant',
  'setElevation',
  'setElevationFlag',
  'setElevationOption',
  'showIndicator',
  'size',
  'startAt',
  'textColor',
  'textSize',
  'variant',
  'width',
]);

/**
 * Content fields a component branches on, that are not presentation settings.
 *
 * `styling === 'button'` is false once `styling` carries an encoded suffix, so the
 * component silently renders its other variant — in draft mode and nowhere else.
 *
 * Kept separate from {@link PRESENTATION_FIELDS} because these come from ordinary content
 * fields and cannot be derived from `useSetting`. Sourced by grepping
 * `libs/cms-components` for equality against string literals; the same spec prints the
 * current candidates so a new one is easy to classify.
 */
export const DISPATCH_FIELDS: ReadonlySet<string> = new Set([
  'availabilityColor',
  'cardTypeOption',
  'displayVariant',
  'energyUsagePurpose',
  'flowName',
  // Design-system lookup keys, resolved by `getIllustration` in `@eneco/cms-components`. An
  // encoded name misses `name in SparkyIllustrations` and falls back to Sparky's `Placeholder`,
  // which draws a grey square — so the page renders, in draft only, with every illustration
  // replaced by a box. `icon` names an illustration too (`MegaMenuV2`), and was previously safe
  // only because `@sanity/client`'s own denylist happens to contain it; listing it here means the
  // behaviour no longer depends on someone else's list.
  'icon',
  'illustration',
  // A list item's Sitecore name, which components use as a key: the inspiration advice filter matches a card's
  // tags against a phrase's by it and builds translation and session-storage keys from it.
  'itemName',
  'kind',
  'role',
  'styling',
]);

/** Everything above, which is what the filter actually consults. */
export const NEVER_ENCODE: ReadonlySet<string> = new Set([
  ...STRUCTURAL_FIELDS,
  ...PRESENTATION_FIELDS,
  ...DISPATCH_FIELDS,
]);

/**
 * Extends `@sanity/client`'s default stega filter rather than replacing it — the default
 * is well-judged and carries far more cases than this ever should (`_`-prefixed fields,
 * dates, URLs, `slug.current`, and a denylist covering `href`/`path`/`type`/`variant`).
 * This only adds the names it cannot know about: ours.
 */
export const stegaFilter: FilterDefault = props => {
  const field = props.sourcePath.at(-1);
  if (typeof field === 'string' && NEVER_ENCODE.has(field)) return false;
  return props.filterDefault(props);
};
